import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from '../base/base-api.service';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  InitResponse,
  UserDto,
  ChangePasswordRequest,
  RequestPasswordResetRequest,
  ConfirmPasswordResetRequest,
  SendActivationLinkRequest,
  ActivateAccountRequest,
  AdminResetPasswordRequest,
  VerifyTwoFactorRequest,
  VerifyTwoFactorResponse,
  RequestTwoFactorCodeRequest,
  UserDeviceDto,
  UserSessionDto,
  RefreshTokenResponse,
} from '@marina/models';
import type {
  UserStatusDto,
  UpdateUserStatusRequest,
} from '@marina/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Login user
   * Tokens are set as httpOnly cookies by the backend.
   * @param request Login request
   * @param deviceToken Optional device token (sent via X-Device-Token header)
   * @param deviceFingerprint Optional device fingerprint (sent via X-Device-Fingerprint header)
   */
  login(
    request: LoginRequest,
    deviceToken?: string | null,
    deviceFingerprint?: string | null
  ): Observable<LoginResponse> {
    let headers = this.getHeadersWithCredentials();
    
    // Add device token header if provided
    if (deviceToken) {
      headers = headers.set('X-Device-Token', deviceToken);
    }
    
    // Add device fingerprint header if provided
    if (deviceFingerprint) {
      headers = headers.set('X-Device-Fingerprint', deviceFingerprint);
    }
    
    return this.post<LoginResponse>('auth/login', request, headers);
  }

  /**
   * Init API - Returns complete user context including accessible marinas and per-marina roles.
   * Called immediately after login to populate frontend state.
   */
  init(): Observable<InitResponse> {
    return this.get<InitResponse>('auth/init', undefined, this.getHeadersWithCredentials());
  }

  /**
   * Register new user
   * Tokens are set as httpOnly cookies by the backend.
   */
  register(request: RegisterRequest): Observable<LoginResponse> {
    return this.post<LoginResponse>('auth/register', request, this.getHeadersWithCredentials());
  }

  /**
   * Logout user
   * Clears httpOnly cookies on the backend.
   */
  logout(): Observable<void> {
    // rawErrors: the caller decides what a failed logout means; it must not arrive as a bare message.
    return this.post<void>('auth/logout', {}, this.getHeadersWithCredentials(), { rawErrors: true });
  }

  /**
   * Refresh authentication token
   * Refresh token is read from httpOnly cookie.
   * New tokens are set as httpOnly cookies by the backend.
   * Returns expiration times for both access and refresh tokens.
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    // rawErrors: refresh failures are classified by status and body — 401/403 ends the session,
    // anything else is temporary. Both reshaping layers would erase that distinction.
    return this.post<RefreshTokenResponse>(
      'auth/refresh', {}, this.getHeadersWithCredentials(), { rawErrors: true });
  }

  /**
   * Tell the server the user is still interacting with the page.
   *
   * Throttled by the caller to at most one a minute, and sent only while input is actually
   * happening. rawErrors so a 401 stays a 401 for the caller to classify.
   */
  recordActivity(): Observable<void> {
    return this.post<void>(
      'auth/sessions/activity', {}, this.getHeadersWithCredentials(), { rawErrors: true });
  }

  /**
   * Get current user info
   */
  getCurrentUser(): Observable<UserDto> {
    return this.get<UserDto>('auth/me', undefined, this.getHeadersWithCredentials());
  }

  /**
   * Change password
   */
  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.post<void>('auth/change-password', request, this.getHeadersWithCredentials());
  }

  /**
   * Request password reset
   */
  requestPasswordReset(request: RequestPasswordResetRequest): Observable<void> {
    return this.post<void>('auth/reset-password', request);
  }

  /**
   * Confirm password reset
   */
  confirmPasswordReset(request: ConfirmPasswordResetRequest): Observable<void> {
    return this.post<void>('auth/reset-password/confirm', request);
  }

  /**
   * Send activation link
   */
  sendActivationLink(request: SendActivationLinkRequest): Observable<void> {
    return this.post<void>('auth/send-activation-link', request);
  }

  /**
   * Activate account with token and password
   */
  activateAccount(request: ActivateAccountRequest): Observable<LoginResponse> {
    return this.post<LoginResponse>('auth/activate', request, this.getHeadersWithCredentials());
  }

  /**
   * Admin reset password
   */
  adminResetPassword(request: AdminResetPasswordRequest): Observable<void> {
    return this.post<void>('auth/admin/reset-password', request, this.getHeadersWithCredentials());
  }

  /**
   * Admin resend activation link
   */
  adminResendActivation(request: SendActivationLinkRequest): Observable<{ message?: string }> {
    return this.post<{ message?: string }>('auth/admin/resend-activation', request, this.getHeadersWithCredentials());
  }

  /**
   * Verify 2FA code
   */
  verifyTwoFactor(request: VerifyTwoFactorRequest): Observable<VerifyTwoFactorResponse> {
    return this.post<VerifyTwoFactorResponse>('auth/verify-2fa', request, this.getHeadersWithCredentials());
  }

  /**
   * Request 2FA code resend
   */
  requestTwoFactorCode(request: RequestTwoFactorCodeRequest): Observable<void> {
    return this.post<void>('auth/request-2fa-code', request, this.getHeadersWithCredentials());
  }

  /**
   * Get user's remembered devices
   * @param deviceToken Optional device token (sent via X-Device-Token header) to identify current device
   */
  getDevices(deviceToken?: string | null): Observable<UserDeviceDto[]> {
    let headers = this.getHeadersWithCredentials();
    
    // Add device token header if provided
    if (deviceToken) {
      headers = headers.set('X-Device-Token', deviceToken);
    }
    
    return this.get<UserDeviceDto[]>('auth/devices', undefined, headers);
  }

  /**
   * Revoke a device
   */
  revokeDevice(deviceId: string): Observable<void> {
    return this.deleteRequest<void>(`auth/devices/${deviceId}`, this.getHeadersWithCredentials());
  }

  /**
   * Get user's active sessions
   */
  getSessions(): Observable<UserSessionDto[]> {
    return this.get<UserSessionDto[]>('auth/sessions', undefined, this.getHeadersWithCredentials());
  }

  /**
   * Revoke a specific session
   */
  revokeSession(sessionId: string): Observable<void> {
    return this.deleteRequest<void>(`auth/sessions/${sessionId}`, this.getHeadersWithCredentials());
  }

  /**
   * Revoke all sessions (logout from all devices)
   */
  revokeAllSessions(): Observable<void> {
    return this.post<void>('auth/sessions/revoke-all', {}, this.getHeadersWithCredentials());
  }

  /**
   * Get user status by email (admin only)
   */
  getUserStatusByEmail(email: string): Observable<UserStatusDto> {
    return this.get<UserStatusDto>(`auth/admin/user-status/${encodeURIComponent(email)}`, undefined, this.getHeadersWithCredentials());
  }

  /**
   * Update user status (admin only)
   */
  updateUserStatus(request: UpdateUserStatusRequest): Observable<void> {
    return this.put<void>('auth/admin/user-status', request, this.getHeadersWithCredentials());
  }

  /**
   * Get HTTP headers with credentials (for httpOnly cookies)
   */
  private getHeadersWithCredentials(): HttpHeaders {
    return this.getHeaders();
  }
}
